import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useEffect, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthActions } from "@convex-dev/auth/react";
import { handleAuthError } from "@/elements/components/utils/auth-error-handler";
import { makeRedirectUri } from "expo-auth-session";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { auth } from "@/elements/components/utils/convex-api";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import EyeOffIcon from "@/assets/svgs/eye-off-icon";
import EyeOpenIcon from "@/assets/svgs/eye-open-icon";
import { StudentCheckSchema } from "./check";
import Toast from "react-native-toast-message";

const StudentPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters long")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .refine(
        (password) => /\d/.test(password) || /[!@#$%^&*(),.?":{}|<>]/.test(password),
        "Password must contain at least one number or symbol"
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type StudentPasswordSchemaType = z.infer<typeof StudentPasswordSchema>;

function StudentPasswordScreen() {
  const { email } = useLocalSearchParams<{ email: string }>();
  const router = useRouter();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      password: "",
    },
    mode: "onChange",
    resolver: zodResolver(StudentPasswordSchema),
  });
  const [showPassword, setShowPassword] = useState(false);
  const { signIn: authorize } = useAuthActions();

  const { data: isAuthenticated = false, isSuccess } = useQuery(
    convexQuery(auth.isAuthenticated, {})
  );

  useEffect(() => {
    if (email) {
      // Validate email with zod schema and redirect if invalid
      const result = StudentCheckSchema.safeParse({ email });
      if (!result.success) {
        router.replace("/(auth)/student/check");
      }
    }
  }, [email, router]);

  if (!isSuccess) {
    return <Loader />;
  }

  if (isAuthenticated) {
    return <Redirect href={"/(app)/home"} />;
  }

  if (!email) {
    return <Redirect href={"/(auth)/student/check"} />;
  }

  const onSubmit = async (data: StudentPasswordSchemaType) => {
    try {
      await AsyncStorage.setItem("persona", "student");
      const success = await authorize("password", {
        email: email as string,
        password: data.password,
        context: "student",
        flow: "signUp",
        redirectTo: makeRedirectUri(),
      });
      if ((success?.signingIn) !== true) {
        router.push({
          pathname: "/(auth)/verify-email" as any,
          params: { email: email as string, persona: "student" },
        });
      }
    } catch (error) {
      const errorResult = handleAuthError(error);
      console.log(errorResult);
      Toast.show({
        type: "error",
        text1: errorResult.message,
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    }
  };

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon
          onPress={() =>
            router.canGoBack()
              ? router.back()
              : router.replace("/(auth)/sign-in-options")
          }
        />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Set your password
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-black leading-[1.5] ">
                Use this password to log in to your account
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-4">
              <Controller
                control={control}
                name="password"
                render={({ field, fieldState }) => (
                  <View className="gap-2">
                    <View>
                      <Input
                        size="md"
                        placeholder="Password"
                        {...field}
                        secureTextEntry={!showPassword}
                        error={!!fieldState.error?.message}
                      />
                      <Pressable
                        onPress={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2"
                      >
                        {showPassword ? (
                          <EyeOpenIcon
                            className="size-6"
                            pathClassName="stroke-gray-400"
                          />
                        ) : (
                          <EyeOffIcon
                            className="size-6"
                            pathClassName="stroke-gray-400"
                          />
                        )}
                      </Pressable>
                    </View>
                    {(!!fieldState.error?.message ||
                      field.value.length === 0) && (
                      <Input.Message>
                        Password must be a minimum 8 characters. {"\n"}Include
                        one capital letter and one number or symbol.
                      </Input.Message>
                    )}
                  </View>
                )}
              />
              <Controller
                control={control}
                name="confirmPassword"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="Confirm password"
                    {...field}
                    secureTextEntry
                    error={!!fieldState.error?.message}
                  />
                )}
              />
            </View>
            <View className="h-8" />
            <Button
              className="w-full"
              size="md"
              intent="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid}
            >
              <Button.Text size="md">Next</Button.Text>
            </Button>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}
export default StudentPasswordScreen;
